import { skipToken } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import {
  extractAssetGroupId,
  makeCacheKey,
  type AssetGroupId,
} from '@infinex/asset-config';
import type { GetAssetGroupPricesOutput } from '@infinex/main-service-contract/v2/client/assets';
import type { Rate } from '@infinex/types';
import { useAuthedQuery } from '@infinex/user';

import type { QueryOptionsV2 } from '../config';
import { getTRPCClient } from '../config';

/** Looks up a single price to leverage React Query cache */
export const useAssetPriceQuery = (
  search: AssetGroupId | null | undefined,
  options?: Omit<
    QueryOptionsV2<GetAssetGroupPricesOutput, Rate | null>,
    'select'
  >
) => {
  const lookup = useMemo(
    () => (search ? extractAssetGroupId(search) : null),
    [search]
  );

  return useAuthedQuery(
    getTRPCClient().assets.getAssetGroupPrices.queryOptions(
      lookup ? { lookups: [lookup] } : skipToken,
      {
        staleTime: 1000 * 15, // 15 seconds
        ...options,
        select: (response) =>
          lookup ? selectAssetGroupPrice(response, lookup) : null,
      }
    )
  );
};

/**
 * used in the swidge feature only, where we inject it as a dependency
 */
export const useBuildFetchAssetPriceApi = () => {
  const getAssetPriceQueryOptions = useCallback((search: AssetGroupId) => {
    const lookup = extractAssetGroupId(search);

    return getTRPCClient().assets.getAssetGroupPrices.queryOptions(
      {
        lookups: [lookup],
      },
      {
        select: (response) => selectAssetGroupPrice(response, lookup),
      }
    );
  }, []);

  return {
    getAssetPriceQueryOptions,
  };
};

function selectAssetGroupPrice(
  response: GetAssetGroupPricesOutput,
  lookup: AssetGroupId
) {
  return response[makeCacheKey(lookup)]?.groupRate ?? null;
}
